// enums
import A4_MM_DIMENSION from "../enums/printPageA4SizeEnum.js";
import getDivHeight from "./getDivHeight.js";
/**
 * Manages the pages of a document, including the addition of sections and handling of page breaks.
 * The class calculates the height of each page and ensures that sections do not overflow the page's height.
 * If a section does not fit on the current page, a new page is started. Page breaks can also be forced after a section.
 *
 * @param {number} margin - The page break buffer size. It is used in calculating the number of section items that fit on a page. Defaults to 25.
 */ class PageManager {
    /**
   * Starts a new page by pushing the current page to the pages array and resetting the page and pageHeight variables.
   */ newPage() {
        this.pages.push(this.page);
        this.page = [];
        this.pageHeight = 0;
    }
    /**
   * Adds a section to the current page. If the section does not fit on the current page, a new page is started.
   * A page break can also be forced after a section. If the section is not visible, it is not added to the page.
   *
   * @param {Object} section - The section to be added. Defaults to an empty object.
   * @returns {number} - The page number where the section was added.
   */ addSection(section = {}) {
        const { child, visible = true, pageBreak } = section;
        const height = (section === null || section === void 0 ? void 0 : section.height) || getDivHeight(section.id);
        if (!child || !visible) {
            if (pageBreak === "after") {
                this.newPage(); // Ensure page break if section is not visible but needs a page break
            }
            return;
        }
        if (this.pageHeight + height > this.maxHeight) {
            this.newPage(); // Overflow detected
        }
        this.page.push(section);
        this.pageHeight += height;
        const pageNumber = this.pages.length + 1;
        if (pageBreak === "after") {
            this.newPage(); // Page break
        }
        return pageNumber;
    }
    /**
   * Returns a copy of the pages array, including the current page if it contains any sections.
   *
   * @returns {Array} - A copy of the pages array.
   */ get() {
        if (this.page.length > 0) {
            this.pages.push(this.page);
        }
        return this.pages.slice();
    }
    constructor(margin = 25){
        this.pages = [];
        this.maxHeight = A4_MM_DIMENSION.HEIGHT * (96 / 24.5) - margin;
        this.page = [];
        this.pageHeight = 0;
    }
}
/**
 * Constructs the pages of a document with the provided sections and a page break buffer (margin).
 * It creates a new PageManager instance, adds each section to it, and sets the order and page number
 * for each section. The function then returns an array containing the updated sections array and the
 * page data from the PageManager.
 *
 * @param {Array} sections - An array of the sections to be included in the pages. Defaults to an empty array.
 * @param {number} margin - The page break buffer size. It is used in calculating the number of section items that fit on a page. Defaults to 25.
 * @returns {Array} - An array where the first element is the updated sections array (with "order" and "pageNumber" properties set) and the second element is the page data from the PageManager.
 */ export default function buildPages(sections = [], margin = 25) {
    const Pages = new PageManager(margin);
    sections.forEach((section = {}, index)=>{
        const pageNumber = Pages.addSection(section);
        sections[index]["order"] = index;
        sections[index]["pageNumber"] = pageNumber;
    });
    return [
        sections,
        Pages.get()
    ];
}
